import Papa from 'papaparse'

import type { CsvRows } from './decodeCsvFile'
import type { DecisionsByLine, DuplicateConflict } from './duplicateDecisions'
import { existingUid } from './duplicateDecisions'
import {
  columnIndex,
  importCsvLayout,
  rowIndexForLine,
  serverCellValue,
} from './importCsvRows'

/**
 * A flagged line has no row in the decoded file, or its row names a different
 * player than the one the server flagged. The file is not sent.
 */
export class DuplicateRowMismatchError extends Error {
  readonly line: number

  constructor(line: number) {
    super(`Row ${line} of the file does not match the row under review.`)
    this.name = 'DuplicateRowMismatchError'
    this.line = line
  }
}

/** The column an "existing" decision writes its uid into. */
export interface DuplicateIdColumn {
  /** The importer's name for it: the header as trimCSVHeaders normalizes it. */
  slug: string
  /** The header added when the file has no such column. */
  header: string
}

export const SN_PLAYER_ID_COLUMN: DuplicateIdColumn = {
  slug: 'sn_player_id',
  header: 'SN Player ID',
}

export const SN_OFFICIAL_ID_COLUMN: DuplicateIdColumn = {
  slug: 'sn_official_id',
  header: 'SN Official ID',
}

/** What the rewrite reads of a flagged row: its line, and the names the server read there. */
export type FlaggedRow = Pick<
  DuplicateConflict,
  'line' | 'first_name' | 'last_name'
>

const padTo = (row: readonly string[], length: number): string[] => {
  const padded = row.slice()
  while (padded.length < length) {
    padded.push('')
  }

  return padded
}

/**
 * The rows to post for the confirm pass, with every decision applied, or null
 * when every decision is "create": the original file then goes up untouched.
 *
 * - "ignore" drops the row.
 * - "existing" writes the uid into `idColumn` (SN Player ID unless told
 *   otherwise). Without that column, one is added after the widest row, so a
 *   cell past the header that the importer ignores today never lands under it.
 *
 * Every flagged row is checked before anything changes: the names in its row
 * must be exactly the names the server flagged. A mismatch means the rows are
 * misaligned, so it throws and nothing is sent. The input is not modified.
 */
export const applyDuplicateDecisions = (
  rows: CsvRows,
  conflicts: readonly FlaggedRow[],
  decisions: DecisionsByLine,
  idColumn: DuplicateIdColumn = SN_PLAYER_ID_COLUMN
): CsvRows | null => {
  if (conflicts.every(({ line }) => decisions[line] === 'create')) {
    return null
  }

  const layout = importCsvLayout(rows)
  const firstName = columnIndex(layout, 'first_name')
  const lastName = columnIndex(layout, 'last_name')

  const rowIndexes = conflicts.map(({ line, first_name, last_name }) => {
    const index = rowIndexForLine(layout, line)
    const row = index === undefined ? undefined : rows[index]
    if (
      index === undefined ||
      row === undefined ||
      serverCellValue(row[firstName]) !== first_name ||
      serverCellValue(row[lastName]) !== last_name
    ) {
      throw new DuplicateRowMismatchError(line)
    }

    return index
  })

  const output = rows.slice()
  let idIndex = columnIndex(layout, idColumn.slug)
  if (idIndex === -1) {
    idIndex = rows.reduce((width, row) => Math.max(width, row.length), 0)
    output[0] = [...padTo(rows[0], idIndex), idColumn.header]
  }

  const dropped = new Set<number>()
  conflicts.forEach(({ line }, i) => {
    const decision = decisions[line]
    const index = rowIndexes[i]

    if (decision === undefined) {
      throw new Error(`Row ${line} has no decision.`)
    }
    if (decision === 'ignore') {
      dropped.add(index)
      return
    }

    const uid = existingUid(decision)
    if (uid !== null) {
      const row = padTo(output[index], output[0].length)
      row[idIndex] = uid
      output[index] = row
    }
  })

  return output.filter((_, index) => !dropped.has(index))
}

/**
 * The file the confirm pass posts: the original File when no row needs
 * rewriting, otherwise the rewritten rows as a UTF-8 file under the same name.
 */
export const confirmUploadFile = (
  original: File,
  rows: CsvRows,
  conflicts: readonly FlaggedRow[],
  decisions: DecisionsByLine,
  idColumn: DuplicateIdColumn = SN_PLAYER_ID_COLUMN
): File => {
  const rewritten = applyDuplicateDecisions(
    rows,
    conflicts,
    decisions,
    idColumn
  )
  if (rewritten === null) {
    return original
  }

  return new File([Papa.unparse(rewritten)], original.name, {
    type: original.type || 'text/csv',
  })
}
